import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { Navigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Loader2 } from 'lucide-react';
import { Plus, Trash, PencilSimple, BookOpen, Handshake, FileText, File, Eye, EyeSlash } from '@phosphor-icons/react';
import { listPortalResourcesAdmin, createPortalResource, updatePortalResource, deletePortalResource } from '../services/api';

const CATEGORIES = [
  { key: 'guide', label: 'Guide pratique', icon: BookOpen, color: 'bg-[#00AEEF]/10 text-[#005f8a]' },
  { key: 'charte', label: 'Charte', icon: Handshake, color: 'bg-[#80C51F]/10 text-[#2D5A4C]' },
  { key: 'modele', label: 'Modèle administratif', icon: FileText, color: 'bg-[#FF8E00]/10 text-[#b56600]' },
  { key: 'autre', label: 'Autre', icon: File, color: 'bg-gray-100 text-gray-700' },
];

const EMPTY = { title: '', category: 'guide', icon: '📄', description: '', content: '', download_url: '', is_public: true };

export default function PortalResourcesAdminPage() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const isAdmin = user && ['super_admin', 'admin', 'direction'].includes(user.role);

  const load = async () => {
    setLoading(true);
    try { const r = await listPortalResourcesAdmin(); setItems(r.data.items || []); }
    catch { toast.error('Erreur de chargement'); }
    setLoading(false);
  };
  useEffect(() => { if (isAdmin) load(); }, [isAdmin]);

  if (!user) return null;
  if (!isAdmin) return <Navigate to="/dashboard" replace />;

  const openNew = () => { setEditing(null); setForm(EMPTY); setDialogOpen(true); };
  const openEdit = (r) => { setEditing(r); setForm({ ...EMPTY, ...r, download_url: r.download_url || '' }); setDialogOpen(true); };

  const save = async () => {
    if (!form.title || form.title.length < 3) return toast.error('Titre requis (min 3 caractères)');
    if (!form.content || form.content.length < 5) return toast.error('Contenu trop court');
    setSaving(true);
    try {
      const payload = { ...form, download_url: form.download_url || null };
      if (editing) await updatePortalResource(editing.id, payload);
      else await createPortalResource(payload);
      toast.success('Ressource enregistrée');
      setDialogOpen(false);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
    setSaving(false);
  };

  const remove = async (r) => {
    if (!window.confirm(`Supprimer « ${r.title} » ?`)) return;
    try { await deletePortalResource(r.id); toast.success('Supprimée'); await load(); }
    catch { toast.error('Erreur'); }
  };

  return (
    <div className="p-6 md:p-8 space-y-5 max-w-5xl" data-testid="resources-admin-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-[#1A2421]" style={{ fontFamily: 'Manrope' }}>Ressources Portail</h1>
          <p className="text-sm text-[#5E6D66]">Guides pratiques, chartes et modèles administratifs partagés avec la coopérative.</p>
        </div>
        <Button onClick={openNew} className="bg-[#2D5A4C] hover:bg-[#1E4136]" data-testid="resource-new">
          <Plus size={14} className="mr-1" /> Nouvelle ressource
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-10 text-[#5E6D66]"><Loader2 className="animate-spin inline mr-1" size={16} /> Chargement…</div>
      ) : items.length === 0 ? (
        <Card className="border-[#E8E6E1]"><CardContent className="p-8 text-center text-[#5E6D66]">Aucune ressource.</CardContent></Card>
      ) : (
        <div className="space-y-2">
          {items.map((r) => {
            const cat = CATEGORIES.find((c) => c.key === r.category) || CATEGORIES[3];
            const Icon = cat.icon;
            return (
              <Card key={r.id} className="border-[#E8E6E1]" data-testid={`resource-card-${r.id}`}>
                <CardContent className="p-4 flex items-start gap-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${cat.color}`}>
                    <Icon size={20} weight="duotone" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-lg">{r.icon || '📄'}</span>
                      <p className="font-semibold text-[#1A2421] truncate">{r.title}</p>
                      <span className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full font-semibold ${cat.color}`}>{cat.label}</span>
                      {r.is_public ? (
                        <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full font-semibold bg-emerald-100 text-emerald-800 flex items-center gap-1"><Eye size={10}/> Publique</span>
                      ) : (
                        <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full font-semibold bg-gray-100 text-gray-600 flex items-center gap-1"><EyeSlash size={10}/> Privée</span>
                      )}
                    </div>
                    <p className="text-sm text-[#5E6D66] mt-1 line-clamp-2">{r.description}</p>
                  </div>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" onClick={() => openEdit(r)} data-testid={`resource-edit-${r.id}`}><PencilSimple size={14}/></Button>
                    <Button variant="ghost" size="icon" onClick={() => remove(r)} className="text-red-600 hover:bg-red-50" data-testid={`resource-del-${r.id}`}><Trash size={14}/></Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl" data-testid="resource-dialog">
          <DialogHeader><DialogTitle>{editing ? 'Modifier la ressource' : 'Nouvelle ressource'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-3 gap-2">
              <div className="col-span-2"><Label>Titre *</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="resource-title"/></div>
              <div><Label>Icône (emoji)</Label><Input value={form.icon} onChange={(e) => setForm({ ...form, icon: e.target.value })} maxLength={4} data-testid="resource-icon"/></div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label>Catégorie</Label>
                <select className="w-full h-9 rounded-md border border-[#E8E6E1] bg-white px-2 text-sm" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="resource-category">
                  {CATEGORIES.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
                </select>
              </div>
              <label className="flex items-end gap-2 pb-2 cursor-pointer">
                <input type="checkbox" checked={form.is_public} onChange={(e) => setForm({ ...form, is_public: e.target.checked })} className="w-4 h-4 accent-[#2D5A4C]" data-testid="resource-public"/>
                <span className="text-sm">Publique (visible dans /portal/library)</span>
              </label>
            </div>
            <div><Label>Description courte</Label><Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="resource-desc"/></div>
            <div>
              <Label>Contenu (Markdown supporté)</Label>
              <Textarea rows={9} value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} data-testid="resource-content" className="font-mono text-xs" placeholder="## Titre&#10;- Point 1&#10;- Point 2"/>
            </div>
            <div><Label>Lien de téléchargement (optionnel)</Label><Input value={form.download_url} onChange={(e) => setForm({ ...form, download_url: e.target.value })} placeholder="https://…" data-testid="resource-url"/></div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDialogOpen(false)}>Annuler</Button>
            <Button disabled={saving} onClick={save} className="bg-[#2D5A4C] hover:bg-[#1E4136]" data-testid="resource-save">
              {saving ? <Loader2 className="animate-spin mr-1" size={14}/> : null}{editing ? 'Enregistrer' : 'Créer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
